import { useCallback, useEffect, useState } from "react";
import type React from "react";
import { dialogTitleStyle } from "./destructiveStyles";

interface SaveToCloudDialogProps {
  saving: boolean;
  description?: string;
  error?: string | null;
  onSave: (name: string) => void;
  onClose: () => void;
}

export default function SaveToCloudDialog({
  saving,
  description,
  error,
  onSave,
  onClose,
}: SaveToCloudDialogProps) {
  const [name, setName] = useState("");

  const close = useCallback(() => {
    if (!saving) onClose();
  }, [onClose, saving]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [close]);

  const submit = () => {
    if (saving) return;
    const trimmed = name.trim();
    if (trimmed) onSave(trimmed);
  };

  return (
    <div style={overlayStyle} onClick={close} role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-cloud-title"
        aria-describedby="save-cloud-description"
        style={dialogStyle}
        data-testid="save-to-cloud-dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="save-cloud-title" style={dialogTitleStyle}>
          Save to cloud
        </h2>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <label id="save-cloud-description" htmlFor="save-cloud-name" style={labelStyle}>
            {description ?? "Provide a title for this drawing."}
          </label>
          <div style={rowStyle}>
            <input
              id="save-cloud-name"
              autoFocus
              value={name}
              maxLength={120}
              disabled={saving}
              onChange={(event) => setName(event.target.value)}
              style={inputStyle}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
              }}
            >
              <span
                role={error ? "alert" : undefined}
                style={{ color: "var(--color-danger)", fontSize: 14 }}
              >
                {error}
              </span>
              <button
                type="submit"
                disabled={saving || !name.trim()}
                style={{
                  ...primaryButtonStyle,
                  ...(saving || !name.trim() ? disabledButtonStyle : null),
                }}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

const EXCALIDRAW_FONT =
  "Assistant, system-ui, BlinkMacSystemFont, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif";

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 10001,
  background: "var(--exms-overlay-bg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const dialogStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 560,
  boxSizing: "border-box",
  background: "var(--island-bg-color)",
  borderRadius: 12,
  border: "1px solid var(--dialog-border-color)",
  boxShadow: "var(--exms-shadow-modal)",
  padding: 24,
  fontFamily: EXCALIDRAW_FONT,
};

const labelStyle: React.CSSProperties = {
  display: "block",
  marginTop: 20,
  marginBottom: 8,
  fontSize: 14,
  fontWeight: 600,
};

const rowStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 12 };

const inputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: "12px 14px",
  borderRadius: 10,
  border: "1px solid var(--dialog-border-color)",
  background: "var(--island-bg-color)",
  color: "inherit",
  fontSize: 15,
  fontFamily: "inherit",
};

const primaryButtonStyle: React.CSSProperties = {
  alignSelf: "flex-end",
  padding: "12px 20px",
  border: "1px solid var(--color-primary)",
  borderRadius: 10,
  background: "var(--color-primary)",
  color: "var(--color-on-accent)",
  fontSize: 15,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
};

const disabledButtonStyle: React.CSSProperties = {
  cursor: "not-allowed",
  opacity: 0.6,
};
